import React, { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { PENDING_CONTACT_LINK_INTENT } from 'constants/loanApplication';
import useIsCrmEnabled from 'hooks/useIsCrmEnabled';

import { showMessageAction } from 'store/global/actions';
import {
  clearPendingBorrowerContactLink,
  setPendingBorrowerContactLink,
  setPendingBorrowerContactUnlink,
} from 'store/loanApplications/actions';
import {
  selectBorrowerCrmContactById,
  selectBorrowerPendingContactLinkById,
} from 'store/loanApplications/selectors';

import { NEW_ENTITY } from '../EditEntityModal/EditEntityModal.constants';

import { CONTACT_PANEL_MODE } from './CrmContactSection.constants';
import { headingStyles, sectionStyles } from './CrmContactSection.styles';
import FindCreatePanel from './FindCreatePanel';
import LinkedContactCard from './LinkedContactCard';
import LinkOrCreateButton from './LinkOrCreateButton';

// The contact shown in the section: a pending unlink hides it, a pending link
// overrides it, otherwise the persisted contact is shown.
const getEffectiveContact = (persistedContact, pending) => {
  if (pending?.action === PENDING_CONTACT_LINK_INTENT.UNLINK) {
    return null;
  }

  if (pending?.action === PENDING_CONTACT_LINK_INTENT.LINK) {
    return pending.contact;
  }

  return persistedContact;
};

// "CRM Contact" section in the Borrower edit sidebar (below the email field).
// Renders the linked contact as an inline card, or an in-place find/create panel
// when none is linked. Linking, creating, and removing are recorded as pending
// changes and only persisted when the borrower's changes are saved.
const CrmContactSection = ({ entityId, firstName = '', lastName = '', email = '', phone = '' }) => {
  const dispatch = useDispatch();

  const [mode, setMode] = useState(CONTACT_PANEL_MODE.COLLAPSED);

  const isCrmEnabled = useIsCrmEnabled();
  const persistedContact = useSelector(state => selectBorrowerCrmContactById(state, entityId));
  const pending = useSelector(state => selectBorrowerPendingContactLinkById(state, entityId));

  const onOpenPanel = useCallback(() => setMode(CONTACT_PANEL_MODE.OPEN), []);
  const onClosePanel = useCallback(() => setMode(CONTACT_PANEL_MODE.COLLAPSED), []);

  // Record the chosen/created contact as a pending link — persisted on save.
  const onContactChosen = useCallback(
    contact => {
      dispatch(setPendingBorrowerContactLink({ borrowerId: entityId, contact }));
      dispatch(
        showMessageAction({
          message: 'Contact will be linked when you save changes.',
          variant: 'success',
        }),
      );
      setMode(CONTACT_PANEL_MODE.COLLAPSED);
    },
    [dispatch, entityId],
  );

  // Removing a pending link just cancels it; removing a persisted contact is
  // recorded as a pending unlink (persisted on save).
  const onRemove = useCallback(() => {
    if (pending?.action === PENDING_CONTACT_LINK_INTENT.LINK) {
      dispatch(clearPendingBorrowerContactLink({ borrowerId: entityId }));

      return;
    }

    if (persistedContact) {
      dispatch(
        setPendingBorrowerContactUnlink({ borrowerId: entityId, contact: persistedContact }),
      );
    }
  }, [dispatch, entityId, pending, persistedContact]);

  if (!isCrmEnabled || entityId === NEW_ENTITY) {
    return null;
  }

  const contact = getEffectiveContact(persistedContact, pending);
  const borrower = { id: entityId, firstName, lastName, email, phone };

  const renderBody = () => {
    if (contact) {
      return <LinkedContactCard contact={contact} onRemove={onRemove} />;
    }

    if (mode === CONTACT_PANEL_MODE.COLLAPSED) {
      return <LinkOrCreateButton onClick={onOpenPanel} />;
    }

    return (
      <FindCreatePanel
        borrower={borrower}
        onClose={onClosePanel}
        onContactChosen={onContactChosen}
      />
    );
  };

  return (
    <Box sx={sectionStyles}>
      <Typography component="h5" sx={headingStyles} variant="medium16">
        CRM Contact
      </Typography>
      {renderBody()}
    </Box>
  );
};

CrmContactSection.propTypes = {
  entityId: PropTypes.string.isRequired,
  email: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  phone: PropTypes.string,
};

export default memo(CrmContactSection);
